import { Search, X } from 'lucide-react';
import type { AssigneeOption, DealFiltersState, Pipeline, StageItem } from './types';

interface DealFilterBarProps {
  filters: DealFiltersState;
  searchInput: string;
  pipelines: Pipeline[];
  stages: StageItem[];
  assignees: AssigneeOption[];
  hasActiveFilters: boolean;
  onSearchInput: (value: string) => void;
  onChange: (patch: Partial<DealFiltersState>) => void;
  onReset: () => void;
}

/**
 * Every control here maps to one query parameter; nothing is filtered in the
 * browser any more. `onChange` sends the patch straight to the page, which
 * rebuilds the query and resets to the first page.
 */
export default function DealFilterBar({
  filters,
  searchInput,
  pipelines,
  stages,
  assignees,
  hasActiveFilters,
  onSearchInput,
  onChange,
  onReset,
}: DealFilterBarProps) {
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSearchInput(event.target.value);
  };

  const handlePipelineChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    // Switching pipelines invalidates the stage filter: stage ids belong to one pipeline.
    onChange({ pipelineId: Number(event.target.value) || null, stageId: null });
  };

  const handleStageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({ stageId: Number(event.target.value) || null });
  };

  const handleStatusChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({ status: event.target.value as DealFiltersState['status'] });
  };

  const handleTempChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({ temp: event.target.value as DealFiltersState['temp'] });
  };

  const handleAssigneeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    onChange({ assignedToId: value === 'all' || value === 'unassigned' ? value : Number(value) });
  };

  const handleSortChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const [sort, order] = event.target.value.split(':');
    onChange({ sort: sort as DealFiltersState['sort'], order: order as DealFiltersState['order'] });
  };

  const handleAmountMinChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ amountMin: event.target.value });
  };

  const handleAmountMaxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ amountMax: event.target.value });
  };

  const handleDateFromChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ dateFrom: event.target.value });
  };

  const handleDateToChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ dateTo: event.target.value });
  };

  const handleClearSearch = () => {
    onSearchInput('');
  };

  return (
    <div className="deals-toolbar">
      <div className="deals-search-input">
        <Search size={16} />
        <input
          type="text"
          placeholder="Поиск по названию, номеру заявки, имени клиента или телефону..."
          value={searchInput}
          onChange={handleSearchChange}
          aria-label="Поиск заявок"
        />
        {searchInput && (
          <button type="button" className="deals-search-clear" onClick={handleClearSearch} aria-label="Очистить поиск">
            <X size={14} />
          </button>
        )}
      </div>

      <div className="deals-filters-row">
        {pipelines.length > 0 && (
          <div className="filter-select-wrapper pipeline-select">
            <select
              value={filters.pipelineId || ''}
              onChange={handlePipelineChange}
              aria-label="Воронка"
              style={{ fontWeight: 600, color: '#4f46e5' }}
            >
              {pipelines.map((pipeline) => (
                <option key={pipeline.id} value={pipeline.id}>
                  Воронка: {pipeline.name} ({pipeline.businessType})
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="filter-select-wrapper">
          <select value={filters.stageId || ''} onChange={handleStageChange} aria-label="Этап воронки">
            <option value="">Все этапы</option>
            {stages
              .filter((stage) => stage.stageId)
              .map((stage) => (
                <option key={stage.stageId} value={stage.stageId}>{stage.label}</option>
              ))}
          </select>
        </div>

        <div className="filter-select-wrapper">
          <select value={filters.status} onChange={handleStatusChange} aria-label="Статус заявки">
            <option value="all">Все статусы</option>
            <option value="open">Открыта (В работе)</option>
            <option value="won">Выиграна (Успех)</option>
            <option value="lost">Проиграна</option>
          </select>
        </div>

        <div className="filter-select-wrapper">
          <select value={filters.temp} onChange={handleTempChange} aria-label="Категория лида">
            <option value="all">Все категории лидов</option>
            <option value="hot">Горячие</option>
            <option value="warm">Тёплые</option>
            <option value="cold">Холодные</option>
          </select>
        </div>

        {assignees.length > 0 && (
          <div className="filter-select-wrapper">
            <select value={String(filters.assignedToId)} onChange={handleAssigneeChange} aria-label="Ответственный">
              <option value="all">Все ответственные</option>
              <option value="unassigned">Без ответственного</option>
              {assignees.map((assignee) => (
                <option key={assignee.id} value={assignee.id}>{assignee.name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="filter-select-wrapper">
          <select value={`${filters.sort}:${filters.order}`} onChange={handleSortChange} aria-label="Сортировка">
            <option value="createdAt:desc">Сначала новые</option>
            <option value="createdAt:asc">Сначала старые</option>
            <option value="amount:desc">Сумма: по убыванию</option>
            <option value="amount:asc">Сумма: по возрастанию</option>
            <option value="title:asc">Название: А—Я</option>
            <option value="title:desc">Название: Я—А</option>
          </select>
        </div>
      </div>

      <div className="deals-filters-row deals-filters-range">
        <label className="deals-range-field">
          <span>Сумма от</span>
          <input type="number" min="0" inputMode="numeric" placeholder="0" value={filters.amountMin} onChange={handleAmountMinChange} />
        </label>
        <label className="deals-range-field">
          <span>до</span>
          <input type="number" min="0" inputMode="numeric" placeholder="—" value={filters.amountMax} onChange={handleAmountMaxChange} />
        </label>
        <label className="deals-range-field">
          <span>Создана с</span>
          <input type="date" value={filters.dateFrom} onChange={handleDateFromChange} />
        </label>
        <label className="deals-range-field">
          <span>по</span>
          <input type="date" value={filters.dateTo} onChange={handleDateToChange} />
        </label>
        {hasActiveFilters && (
          <button type="button" className="deals-reset-filters" onClick={onReset}>
            Сбросить фильтры
          </button>
        )}
      </div>
    </div>
  );
}
